import {tokens} from '@pleo-io/telescope'
import styled, {css} from 'styled-components'

import checkIconPink from '@/images/icons/check-pink.svg'
import numberOneTrophy from '@/images/icons/number-one-trophy.svg'
import {Text, StyledText, text} from '@helios/shared/styles/typography'
import {colors, fonts, radii} from '@helios/shared/theme'
import mq from '@helios/shared/utils/media-query'

import {Props} from './pricing'

export const Row = styled.div(() => {
    return css`
        position: relative;
        display: grid;
        grid-template-columns: 1fr;
        column-gap: ${tokens.spacing20};

        ${mq.min('sm')} {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    `
})

export const Box = styled.div`
    position: relative;
    padding-right: ${tokens.spacing20};
    padding-left: ${tokens.spacing20};
    background-color: ${colors.background.default};

    ${mq.max('sm')} {
        ${Row}:nth-of-type(2) & {
            padding-top: ${tokens.spacing20};
        }

        ${Row}:last-of-type & {
            padding-bottom: ${tokens.spacing20};
        }
    }

    ${mq.min('sm')} {
        ${Row}:first-of-type & {
            padding-top: ${tokens.spacing40};
        }

        ${Row}:last-of-type & {
            padding-bottom: ${tokens.spacing40};
        }
    }

    ${mq.min('lg')} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

export const TiersTitle = styled(Box)`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
`

export const Title = styled(Row)`
    ${Box} {
        align-items: center;
        display: flex;
        padding-bottom: ${tokens.spacing12};

        :nth-child(2) {
            position: relative;

            :after {
                position: absolute;
                top: -${tokens.spacing20};
                right: ${tokens.spacing20};
                width: 80px;
                height: 80px;
                content: '';
                background-color: ${colors.background.default};
                background-image: url(${numberOneTrophy});
                background-repeat: no-repeat;
                background-position: center;
                background-size: 80%;
                border-radius: ${radii.arc8};
                box-shadow: ${tokens.shadowRaiseSubtle};

                ${mq.min('lg')} {
                    right: ${tokens.spacing40};
                }
            }
        }
    }

    ${mq.max('sm')} {
        display: none;
    }
`
type AmountProps = Pick<Props, 'withCtas'>

export const Amount = styled(Row)<AmountProps>`
    ${Box} {
        display: grid;
        align-content: start;
        padding-bottom: ${tokens.spacing32};

        ${mq.min('sm')} {
            padding-bottom: ${(props) => (props.withCtas ? tokens.spacing20 : tokens.spacing40)};
        }
    }
`

export const AmountText = styled(Text)`
    del {
        color: ${colors.text.extraMuted};
    }
    ins {
        text-decoration: none;
    }
`

export const AdditionalUsersModalText = styled(Text)`
    padding: 0;
    color: ${colors.text.lightMuted};
    text-decoration: underline;
    cursor: pointer;
    background: transparent;
    border: none;

    &:hover {
        background: ${colors.background.pink};
    }
`

export const Excerpt = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing32};

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
        }
    }
`

export const ExcerptText = styled(Text)`
    color: ${colors.text.lightMuted};
`

export const Cta = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing32};

        > * {
            width: 100%;

            ${mq.min('xs')} {
                width: auto;
            }
        }

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing40};
        }
    }
`

export const FeaturesTitle = styled(Row)`
    ${Box} {
        padding-bottom: ${tokens.spacing12};

        ${mq.min('sm')} {
            padding-bottom: ${tokens.spacing20};
        }
    }
`

export const FeaturesList = styled.ul`
    display: grid;
    padding: 0;
    margin: 0;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing24};
    }
`

interface FeaturesItemProps {
    $isHighlighted: boolean
    $isDisabled: boolean
}

export const FeaturesItem = styled.li<FeaturesItemProps>(({$isHighlighted, $isDisabled}) => {
    const highlightedStyles = css`
        color: ${colors.text.brand};
    `

    const disabledStyles = css`
        color: ${colors.text.extraMuted};
        text-decoration: line-through;

        /* This combination's outcome is #CCC */
        :before {
            filter: grayscale(1);
            opacity: 0.42;
        }
    `

    return css`
        display: flex;
        ${text({size: 'xs', font: 'secondary'})}
        ${$isHighlighted && highlightedStyles}
        ${$isDisabled && disabledStyles}

        :before {
            margin-right: ${tokens.spacing12};
            content: url(${checkIconPink});
        }
    `
})

export const Tabs = styled.nav`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: ${tokens.spacing8};
    gap: ${tokens.spacing8};

    ${mq.min('sm')} {
        display: none;
    }
`

const isActive = css`
    ${StyledText} {
        font-weight: ${fonts.weights.semiBold};
        text-decoration: underline;
    }
`

interface TabProps {
    isActive: boolean
}

export const Tab = styled.button<TabProps>`
    ${text({color: 'default'})}
    padding: ${tokens.spacing20} 0;
    cursor: pointer;
    background: ${colors.background.default};
    border: none;

    ${(props) => props.isActive && isActive}
`

export const OuterWrapper = styled.div`
    position: relative;
    width: 100%;
`

interface WrapperProps {
    activeItemIndex: number
}

export const Wrapper = styled.div<WrapperProps>`
    ${mq.max('sm')} {
        ${Box}:not(:nth-of-type(${(props) => props.activeItemIndex + 1})) {
            display: none;
        }
    }
`
